import { Duration, ZoneId } from '@js-joda/core';
import camelCase from 'lodash/camelCase';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import pickBy from 'lodash/pickBy';
import pluralize from 'pluralize';
import {
  CREATE,
  CreateResult,
  DELETE,
  DeleteResult,
  GET_LIST,
  GET_MANY,
  GET_MANY_REFERENCE,
  GET_ONE,
  GetListResult,
  GetManyResult,
  GetOneResult,
  UPDATE,
} from 'react-admin';

import { contentComponents } from '../components/Admin/Content';
import {
  AdminCohortDetailsFragment,
  AdminCreateChallengeDocument,
  AdminCreateChatRoomDocument,
  AdminCreateClientDocument,
  AdminCreateCohortDocument,
  AdminCreateContentGenreDocument,
  AdminCreateDomainDocument,
  AdminCreateEligibleEmailAddressDocument,
  AdminCreateExerciseBaseDocument,
  AdminCreateExerciseDocument,
  AdminCreateExerciseTypeCategoryDocument,
  AdminCreateExerciseTypeCategoryGroupDocument,
  AdminCreateExerciseTypeDocument,
  AdminCreateFeatureDocument,
  AdminCreateFeatureGrantDocument,
  AdminCreateLocationDocument,
  AdminCreateMboContractMappingDocument,
  AdminCreateNotificationOptionDocument,
  AdminCreateOfficeDocument,
  AdminCreateStudioDocument,
  AdminCreateTrainingPlanTemplateDocument,
  AdminCreateUserDocument,
  AdminDeleteUserDocument,
  AdminEligibleEmailDetailsFragment,
  AdminGetChallengeDocument,
  AdminGetChatRoomDocument,
  AdminGetClientDocument,
  AdminGetCohortDocument,
  AdminGetContentDocument,
  AdminGetContentGenreDocument,
  AdminGetDomainDocument,
  AdminGetEligibleEmailDocument,
  AdminGetExerciseBaseDocument,
  AdminGetExerciseDocument,
  AdminGetExerciseTypeCategoryDocument,
  AdminGetExerciseTypeCategoryGroupDocument,
  AdminGetExerciseTypeDocument,
  AdminGetFeatureDocument,
  AdminGetFeatureGrantDocument,
  AdminGetLocationDocument,
  AdminGetMboContractMappingDocument,
  AdminGetNotificationOptionDocument,
  AdminGetOfficeDocument,
  AdminGetStudioDocument,
  AdminGetTrainingPlanTemplateDocument,
  AdminGetUserDocument,
  AdminListAuditLogsDocument,
  AdminListChallengesDocument,
  AdminListChatRoomsDocument,
  AdminListClientsDocument,
  AdminListCohortsDocument,
  AdminListContentDocument,
  AdminListContentGenresDocument,
  AdminListDomainsDocument,
  AdminListEligibleEmailsDocument,
  AdminListExerciseBasesDocument,
  AdminListExercisesDocument,
  AdminListExerciseTypeCategoriesDocument,
  AdminListExerciseTypeCategoryGroupsDocument,
  AdminListExerciseTypesDocument,
  AdminListFeatureGrantsDocument,
  AdminListFeaturesDocument,
  AdminListLocationsDocument,
  AdminListMboContractMappingsDocument,
  AdminListNotificationOptionsDocument,
  AdminListOfficesDocument,
  AdminListStudiosDocument,
  AdminListTrainingPlanTemplatesDocument,
  AdminListUsersDocument,
  AdminManyChallengesDocument,
  AdminManyChatRoomsDocument,
  AdminManyClientsDocument,
  AdminManyCohortsDocument,
  AdminManyContentGenresDocument,
  AdminManyDomainsDocument,
  AdminManyEligibleEmailsDocument,
  AdminManyExerciseBasesDocument,
  AdminManyExerciseTypeCategoriesDocument,
  AdminManyExerciseTypeCategoryGroupsDocument,
  AdminManyExerciseTypesDocument,
  AdminManyFeaturesDocument,
  AdminManyLocationsDocument,
  AdminManyMboContractMappingsDocument,
  AdminManyOfficesDocument,
  AdminManyStudiosDocument,
  AdminManyUsersDocument,
  AdminRemoveChallengeDocument,
  AdminRemoveCohortDocument,
  AdminRemoveDomainDocument,
  AdminRemoveEligibleEmailAddressDocument,
  AdminRemoveExerciseDocument,
  AdminRemoveFeatureGrantDocument,
  AdminRemoveMboContractMappingDocument,
  AdminRemoveStudioDocument,
  AdminUpdateChallengeDocument,
  AdminUpdateChatRoomDocument,
  AdminUpdateClientDocument,
  AdminUpdateCohortDocument,
  AdminUpdateContentGenreDocument,
  AdminUpdateDomainDocument,
  AdminUpdateEligibleEmailAddressDocument,
  AdminUpdateExerciseBaseDocument,
  AdminUpdateExerciseDocument,
  AdminUpdateExerciseTypeCategoryDocument,
  AdminUpdateExerciseTypeCategoryGroupDocument,
  AdminUpdateExerciseTypeDocument,
  AdminUpdateFeatureDocument,
  AdminUpdateFeatureGrantDocument,
  AdminUpdateLocationDocument,
  AdminUpdateMboContractMappingDocument,
  AdminUpdateNotificationOptionDocument,
  AdminUpdateOfficeDocument,
  AdminUpdateStudioDocument,
  AdminUpdateTrainingPlanTemplateDocument,
  AdminUpdateUserDocument,
  Client,
  CoachType,
  ContentOrder,
  ContentOrderBy,
  Country,
  DistanceUnit,
  ExerciseBaseOrder,
  ExerciseBaseOrderBy,
  ExerciseOrder,
  ExerciseOrderBy,
  ExerciseTypeCategoryGroupOrder,
  ExerciseTypeCategoryGroupOrderBy,
  ExerciseTypeCategoryOrder,
  ExerciseTypeCategoryOrderBy,
  ExerciseTypeOrder,
  ExerciseTypeOrderBy,
  Genre,
  Node,
  OrderDirection,
  PageInfo,
  User,
} from '../graphql/types';
import { formatImportDate } from '../utils/date';
import { toHpcEligibilityInput } from './hpcEligibility';

const docs = {
  createChatRoom: AdminCreateChatRoomDocument,
  updateChatRoom: AdminUpdateChatRoomDocument,
  manyUsers: AdminManyUsersDocument,
  manyEligibleEmailAddresses: AdminManyEligibleEmailsDocument,
  manyClients: AdminManyClientsDocument,
  manyChatRooms: AdminManyChatRoomsDocument,
  users: AdminListUsersDocument,
  eligibleEmailAddresses: AdminListEligibleEmailsDocument,
  removeEligibleEmailAddress: AdminRemoveEligibleEmailAddressDocument,
  clients: AdminListClientsDocument,
  chatRooms: AdminListChatRoomsDocument,
  user: AdminGetUserDocument,
  eligibleEmailAddress: AdminGetEligibleEmailDocument,
  client: AdminGetClientDocument,
  chatRoom: AdminGetChatRoomDocument,
  createUser: AdminCreateUserDocument,
  createEligibleEmailAddress: AdminCreateEligibleEmailAddressDocument,
  updateUser: AdminUpdateUserDocument,
  updateEligibleEmailAddress: AdminUpdateEligibleEmailAddressDocument,
  deleteUser: AdminDeleteUserDocument,
  createClient: AdminCreateClientDocument,
  updateClient: AdminUpdateClientDocument,

  studios: AdminListStudiosDocument,
  studio: AdminGetStudioDocument,
  createStudio: AdminCreateStudioDocument,
  updateStudio: AdminUpdateStudioDocument,
  manyStudios: AdminManyStudiosDocument,
  removeStudio: AdminRemoveStudioDocument,

  domain: AdminGetDomainDocument,
  domains: AdminListDomainsDocument,
  manyDomains: AdminManyDomainsDocument,
  removeDomain: AdminRemoveDomainDocument,
  createDomain: AdminCreateDomainDocument,
  updateDomain: AdminUpdateDomainDocument,

  coachLocation: AdminGetLocationDocument,
  coachLocations: AdminListLocationsDocument,
  manyCoachLocations: AdminManyLocationsDocument,
  createCoachLocation: AdminCreateLocationDocument,
  updateCoachLocation: AdminUpdateLocationDocument,

  office: AdminGetOfficeDocument,
  offices: AdminListOfficesDocument,
  manyOffices: AdminManyOfficesDocument,
  createOffice: AdminCreateOfficeDocument,
  updateOffice: AdminUpdateOfficeDocument,

  cohort: AdminGetCohortDocument,
  cohorts: AdminListCohortsDocument,
  manyCohorts: AdminManyCohortsDocument,
  createCohort: AdminCreateCohortDocument,
  updateCohort: AdminUpdateCohortDocument,
  removeCohort: AdminRemoveCohortDocument,

  feature: AdminGetFeatureDocument,
  features: AdminListFeaturesDocument,
  manyFeatures: AdminManyFeaturesDocument,
  createFeature: AdminCreateFeatureDocument,
  updateFeature: AdminUpdateFeatureDocument,

  featureGrant: AdminGetFeatureGrantDocument,
  featureGrants: AdminListFeatureGrantsDocument,
  createFeatureGrant: AdminCreateFeatureGrantDocument,
  updateFeatureGrant: AdminUpdateFeatureGrantDocument,
  removeFeatureGrant: AdminRemoveFeatureGrantDocument,

  challenge: AdminGetChallengeDocument,
  challenges: AdminListChallengesDocument,
  updateChallenge: AdminUpdateChallengeDocument,
  createChallenge: AdminCreateChallengeDocument,
  manyChallenges: AdminManyChallengesDocument,
  removeChallenge: AdminRemoveChallengeDocument,

  contentGenre: AdminGetContentGenreDocument,
  contentGenres: AdminListContentGenresDocument,
  manyContentGenres: AdminManyContentGenresDocument,
  updateContentGenre: AdminUpdateContentGenreDocument,
  createContentGenre: AdminCreateContentGenreDocument,

  content: AdminGetContentDocument,
  contents: AdminListContentDocument,

  exercises: AdminListExercisesDocument,
  exerciseBases: AdminListExerciseBasesDocument,
  exerciseTypeCategories: AdminListExerciseTypeCategoriesDocument,
  exerciseTypeCategoryGroups: AdminListExerciseTypeCategoryGroupsDocument,
  exerciseTypes: AdminListExerciseTypesDocument,
  manyExerciseBases: AdminManyExerciseBasesDocument,
  manyExerciseTypeCategories: AdminManyExerciseTypeCategoriesDocument,
  manyExerciseTypeCategoryGroups: AdminManyExerciseTypeCategoryGroupsDocument,
  manyExerciseTypes: AdminManyExerciseTypesDocument,
  exerciseBase: AdminGetExerciseBaseDocument,
  exerciseTypeCategory: AdminGetExerciseTypeCategoryDocument,
  exerciseTypeCategoryGroup: AdminGetExerciseTypeCategoryGroupDocument,
  exerciseType: AdminGetExerciseTypeDocument,
  exercise: AdminGetExerciseDocument,
  updateExercise: AdminUpdateExerciseDocument,
  updateExerciseBase: AdminUpdateExerciseBaseDocument,
  updateExerciseTypeCategory: AdminUpdateExerciseTypeCategoryDocument,
  updateExerciseTypeCategoryGroup: AdminUpdateExerciseTypeCategoryGroupDocument,
  updateExerciseType: AdminUpdateExerciseTypeDocument,
  createExercise: AdminCreateExerciseDocument,
  createExerciseBase: AdminCreateExerciseBaseDocument,
  createExerciseTypeCategory: AdminCreateExerciseTypeCategoryDocument,
  createExerciseTypeCategoryGroup: AdminCreateExerciseTypeCategoryGroupDocument,
  createExerciseType: AdminCreateExerciseTypeDocument,
  removeExercise: AdminRemoveExerciseDocument,

  mboContractMapping: AdminGetMboContractMappingDocument,
  mboContractMappings: AdminListMboContractMappingsDocument,
  manyMboContractMappings: AdminManyMboContractMappingsDocument,
  createMboContractMapping: AdminCreateMboContractMappingDocument,
  updateMboContractMapping: AdminUpdateMboContractMappingDocument,
  removeMboContractMapping: AdminRemoveMboContractMappingDocument,

  notificationOption: AdminGetNotificationOptionDocument,
  notificationOptions: AdminListNotificationOptionsDocument,
  createNotificationOption: AdminCreateNotificationOptionDocument,
  updateNotificationOption: AdminUpdateNotificationOptionDocument,

  auditLogs: AdminListAuditLogsDocument,

  adminTrainingPlanTemplates: AdminListTrainingPlanTemplatesDocument,
  trainingPlan: AdminGetTrainingPlanTemplateDocument,
  createTrainingPlanTemplate: AdminCreateTrainingPlanTemplateDocument,
  updateTrainingPlanTemplate: AdminUpdateTrainingPlanTemplateDocument,
};

const getDoc = (name: keyof typeof docs, type: string) => {
  if (name in docs) {
    return docs[name];
  }

  throw new Error(`${name} ${type} not found`);
};

type QueryHelpers = {
  buildListVariables: (resource: any, params: any) => any;
  parseListPageInfo: (
    resource: any,
    request: any,
    response: any,
  ) => PageInfo | undefined;
};

const updateOrDeleteImage = (image: {
  key: string | null;
  url: string | null;
}) => {
  if (image) {
    // If the image has a key, it means we need to update it
    if (image.key) {
      return image.key;
    } else {
      // If the image has no key, do nothing.  This happens when a non-image field is updated
      return undefined;
    }
  }
  // If the image is null, we delete it
  return null;
};

/** Normalizes exercise records from GraphQL for react-admin (media URLs). */
const formatExerciseRecord = (exercise: Record<string, unknown>) => ({
  ...exercise,
  video: exercise.video
    ? {
        url: exercise.video,
      }
    : null,
  thumbnail: exercise.thumbnail
    ? {
        url: exercise.thumbnail,
      }
    : null,
});

const mapQualifyingChallengeGenres = (
  challengeType: string,
  qualifyingGenres?: string[],
): string[] => {
  if (challengeType === 'GymArrival') {
    return ['gym_arrival'];
  } else if (challengeType === 'StepCount') {
    return [];
  } else {
    return qualifyingGenres ?? [];
  }
};

const buildQuery =
  ({ buildListVariables, parseListPageInfo }: QueryHelpers) =>
  (introspectionResults: any) =>
  (raFetchType: any, resourceName: string, params: any) => {
    const resource = introspectionResults.resources.find(
      (r: any) => r.type.name === resourceName,
    );

    if (!resource) {
      throw new Error(`Resource not found for ${resourceName}`);
    }

    if (!resource[raFetchType]?.name) {
      throw new Error(`Document not found for ${raFetchType}: ${resourceName}`);
    }

    const query = getDoc(resource[raFetchType].name, raFetchType);

    const userFormatter = (user: User) => ({
      ...user,
      chatRooms: {
        ...user.chatRooms,
        ids: (user.chatRooms?.nodes ?? []).map(({ id }: Node) => id),
      },
      coachData: user.coachData
        ? {
            ...user.coachData,
            locations: {
              ...user.coachData.locations,
              ids: (user.coachData.locations?.nodes ?? []).map(
                ({ id }: Node) => id,
              ),
            },
            clients: {
              ...user.coachData.clients,
              ids: (user.coachData.clients?.nodes ?? []).map(
                ({ id }: Node) => id,
              ),
            },
            supportedCountriesAlpha2s: (
              user.coachData.supportedCountries ?? []
            ).map(({ alpha2 }: Country) => alpha2),
            supportGoalTypes: user.coachData.supportedGoals.map(
              ({ type }) => type,
            ),
          }
        : undefined,
    });

    const withClientImportDates = <
      T extends Pick<Client, 'lastFileImportAt' | 'lastMboImportAt' | 'users'>,
    >(
      client: T,
    ) => ({
      ...client,
      users: {
        ...client.users,
        lastMboImportDate: formatImportDate(client.lastMboImportAt),
        lastFileImportDate: formatImportDate(client.lastFileImportAt),
      },
    });

    const formatClientListRecord = (client: Client) =>
      withClientImportDates(client);

    const clientFormatter = (client: Client) => {
      const withImportDates = withClientImportDates(client);
      return {
        ...withImportDates,
        inactivityDuration: client.inactivityDuration
          ? Duration.parse(client.inactivityDuration).toMinutes()
          : null,
        users: {
          ...withImportDates.users,
          ids: client.users.nodes.map(({ id }: Node) => id),
        },
        coaches: {
          ...client.coaches,
          ids: client.coaches.nodes.map(({ id }: Node) => id),
        },
        studios: {
          nodes: client.studios,
          ids: client.studios.map(({ id }: Node) => id),
        },
        domains: {
          nodes: client.domains.nodes,
          ids: client.domains.nodes.map(({ id }: Node) => id),
        },
      };
    };

    const cohortFormatter = (cohort: AdminCohortDetailsFragment) => ({
      ...cohort,
      coaches: {
        ...cohort.coaches,
        ids: cohort.coaches.nodes.map(({ id }) => id),
      },
    });

    const eligibleEmailFormatter = (
      eligibleEmail: AdminEligibleEmailDetailsFragment,
    ) => ({
      ...eligibleEmail,
      // required for correct field reset when removing a cohort from the picker
      cohortId: eligibleEmail.cohort?.id ?? null,
    });

    const formatCoachClientsUpdates = (user: User) => {
      if (user.coachData?.coachClients) {
        const config = user.coachData.coachClients.edges.map((edge: any) => ({
          clientId: edge.node.id,
          supportsHighPerformanceCoaching:
            edge.supportsHighPerformanceCoaching ?? false,
          supportsWellbeingCoaching: edge.supportsWellbeingCoaching ?? true,
          availableForAssignment: edge.availableForAssignment,
        }));
        return config;
      }
      return undefined;
    };

    switch (raFetchType) {
      case 'GET_ONE':
        return {
          query,
          variables: { ...params, timeZone: ZoneId.systemDefault() },
          parseResponse: (response: any): GetOneResult => {
            switch (resource.type.name) {
              case 'Genre':
                return {
                  data: {
                    ...response.data.data,
                    children: {
                      ...response.data.data.children,
                      ids: response.data.data.children.map(
                        (child: Genre) => child.id,
                      ),
                    },
                  },
                };
              case 'ChatRoom':
                return {
                  data: {
                    ...response.data.data,
                    users: {
                      ...response.data.data.users,
                      ids: response.data.data.users.nodes.map(
                        ({ id }: Node) => id,
                      ),
                    },
                  },
                };
              case 'Studio':
                return {
                  data: {
                    ...response.data.data,
                    locations: {
                      ...response.data.data.locations,
                      ids: response.data.data.locations.nodes.map(
                        ({ id }: Node) => id,
                      ),
                    },
                    mboContractMappings: {
                      ...response.data.data.mboContractMappings,
                      ids: response.data.data.mboContractMappings.nodes.map(
                        ({ id }: Node) => id,
                      ),
                    },
                  },
                };
              case 'Challenge':
                return {
                  data: {
                    ...response.data.data,
                    clients: {
                      ...response.data.data.clients,
                      ids: (response.data.data.clients?.nodes ?? []).map(
                        ({ id }: Node) => id,
                      ),
                    },
                  },
                };
              case 'User':
                return {
                  data: userFormatter(response.data.data),
                };
              case 'Client':
                return {
                  data: clientFormatter(response.data.data),
                };
              case 'Exercise':
                return {
                  data: formatExerciseRecord(response.data.data),
                };
              case 'Cohort':
                return {
                  data: cohortFormatter(response.data.data),
                };
              case 'EligibleEmailAddress':
                return {
                  data: eligibleEmailFormatter(response.data.data),
                };
              default:
                return response.data;
            }
          },
        };
      case 'GET_LIST':
        return {
          query,
          variables: buildListVariables(resource, params),
          parseResponse: (response: any): GetListResult => {
            switch (resourceName) {
              case 'Genre':
                return {
                  data: response.data.data.map((d: any) => ({
                    ...d,
                    numberChildren: d.children.length,
                  })),
                  total: response.data.data.count,
                };
              case 'User':
                return {
                  data: response.data.data.nodes.map(userFormatter),
                  total: response.data.data.count,
                  pageInfo: parseListPageInfo(resource, params, response),
                };
              case 'Client':
                return {
                  data: response.data.data.nodes.map(formatClientListRecord),
                  total: response.data.data.count,
                  pageInfo: parseListPageInfo(resource, params, response),
                };
              default:
                return {
                  data: response.data.data.nodes,
                  total: response.data.data.count,
                  pageInfo: parseListPageInfo(resource, params, response),
                };
            }
          },
        };
      case 'GET_MANY':
        return {
          query,
          variables: params,
          parseResponse: (response: any): GetManyResult => {
            switch (resource.type.name) {
              case 'ChatRoom':
                return {
                  data: response.data.data.map((d: any) => ({
                    ...d,
                    users: {
                      ...d.users,
                      ids: d.users.nodes.map(({ id }: Node) => id),
                    },
                  })),
                };
              default:
                return {
                  data: response.data.data,
                };
            }
          },
        };
      case 'CREATE': {
        let createVariables;

        switch (resource.type.name) {
          case 'User':
            createVariables = {
              input: {
                ...params.data,
                adminCreate: true,
              },
            };
            break;
          case 'Client':
            createVariables = {
              input: {
                ...params.data,
                inactivityDuration: params.data.inactivityDuration
                  ? Duration.ofMinutes(params.data.inactivityDuration)
                  : null,
              },
            };
            break;
          case 'Studio':
            createVariables = {
              input: {
                ...params.data,
                isVirtual: !!params.data.isVirtual,
                isCentral: !!params.data.isCentral,
                canGrantEligibility: !!params.data.canGrantEligibility,
                __typename: undefined,
                locations: undefined,
                client: undefined,
                clientId: params.data.client.id,
              },
            };
            break;
          case 'CoachLocation':
            createVariables = {
              input: {
                ...omit(params.data, ['gymImage']),
                isHeadquarters: params.data.isHeadquarters ?? false,
                gymImageId: updateOrDeleteImage(params.data.gymImage),
                __typename: undefined,
                studio: undefined,
                studioId: params.data.studio.id,
              },
            };
            break;
          case 'Office':
            createVariables = {
              input: {
                clientId: params.data.client.id,
                locationId: params.data.location.id,
                name: params.data.name,
                clientOfficeId: params.data.clientOfficeId,
                isFcmEligible: params.data.isFcmEligible,
                isAppEligible: params.data.isAppEligible,
              },
            };
            break;
          case 'Cohort':
            createVariables = {
              input: {
                name: params.data.name,
                startDate: params.data.startDate,
                endDate: params.data.endDate,
                coachIds: params.data.coaches?.ids,
              },
            };
            break;
          case 'Domain':
            createVariables = {
              input: {
                domain: params.data.domain,
                clientId: params.data.client?.id ?? undefined,
              },
            };
            break;
          case 'Feature':
            createVariables = {
              input: {
                key: params.data.key,
                name: params.data.name,
                description: params.data.description || null,
                defaultState: params.data.defaultState,
                requiresFeatureFlagKey:
                  params.data.requiresFeatureFlagKey || null,
              },
            };
            break;
          case 'FeatureGrant':
            createVariables = {
              input: {
                featureId: params.data.feature.id,
                clientId: params.data.client.id,
                scopeType: params.data.scopeType,
                // Ignored server-side for client scope (forced to clientId).
                scopeId: params.data.scopeId || null,
                state: params.data.state,
              },
            };
            break;
          case 'EligibleEmailAddress':
            createVariables = {
              input: {
                email: params.data.email,
                isArchived: !!params.data.isArchived,
                isStaff: !!params.data.isStaff,
                clientId: params.data.client?.id ?? undefined,
                ...toHpcEligibilityInput(params.data),
                officeId: params.data.officeId ?? null,
                isFcmEligible: params.data.manualEligibility
                  ? params.data.isFcmEligible
                  : undefined,
                isAppEligible: params.data.manualEligibility
                  ? params.data.isAppEligible
                  : undefined,
              },
            };
            break;
          case 'Genre':
            createVariables = {
              input: {
                ...params.data,
                parentId: params.data.parentId ?? null,
                isHidden: !!params.data.isHidden,
              },
            };
            break;
          case 'Challenge':
            createVariables = {
              input: {
                ...omit(params.data, ['background', 'badge', 'url', 'clients']),
                qualifyingGenres: mapQualifyingChallengeGenres(
                  params.data.challengeType,
                  params.data.qualifyingGenres,
                ),
                featured: params.data.featured ?? false,
                team: params.data.team ?? false,
                enabled: params.data.enabled ?? false,
                badgeId: updateOrDeleteImage(params.data.badge),
                backgroundId: updateOrDeleteImage(params.data.background),

                clientIds: params.data.clients?.ids ?? [],
                clientSpecific: params.data.clientSpecific ?? false,
                teams: params.data.teams?.nodes ?? [],
                distanceUnit:
                  params.data.challengeType === 'Distance'
                    ? DistanceUnit.Miles
                    : undefined,
              },
            };
            break;
          case 'Exercise': {
            const { exerciseBase, ...exerciseCreateRest } = params.data;
            createVariables = {
              input: {
                ...exerciseCreateRest,
                video: params.data.video?.key,
                thumbnail: params.data.thumbnail?.key,
                defaultUnit: params.data.defaultUnit?.value ?? null,
                exerciseBaseId: exerciseBase?.id ?? undefined,
                __typename: undefined,
              },
            };
            break;
          }
          case 'MboContractMapping':
            createVariables = {
              input: {
                studioId: params.data.studio.id,
                mboServiceId: params.data.mboServiceId,
                employeeType: params.data.employeeType,
              },
            };
            break;
          case 'TrainingPlan':
            createVariables = {
              input: {
                name: params.data.name,
                difficulty: params.data.difficulty,
                frequency: params.data.frequency,
                durationMinutes: params.data.durationMinutes,
                phase: params.data.phase,
                type: params.data.type ?? null,
                equipmentCategory: params.data.equipmentCategory ?? null,
                hidden: params.data.hidden ?? true,
                weeks: params.data.weeks,
              },
            };
            break;
          default:
            createVariables = {
              input: { ...params.data },
            };
        }
        return {
          query,
          variables: createVariables,
          parseResponse: (response: any): CreateResult => {
            switch (resource.type.name) {
              case 'Client': {
                const client =
                  response.data.data[camelCase(resource.type.name)];
                return {
                  data: {
                    ...client,
                    studios: {
                      nodes: client.studios,
                      ids: client.studios.map(({ id }: Node) => id),
                    },
                  },
                };
              }
              case 'Exercise':
                return {
                  data: formatExerciseRecord(
                    response.data.data[camelCase(resource.type.name)],
                  ),
                };
              case 'Cohort':
                return { data: cohortFormatter(response.data.data.cohort) };
              case 'EligibleEmailAddress':
                return {
                  data: eligibleEmailFormatter(
                    response.data.data.eligibleEmailAddress,
                  ),
                };
              case 'TrainingPlan':
                return { data: response.data.data };
              default:
                return {
                  data: response.data.data[camelCase(resource.type.name)],
                };
            }
          },
        };
      }
      case 'UPDATE': {
        let variables;

        switch (resource.type.name) {
          case 'ChatRoom':
            variables = {
              input: {
                id: params.data.id,
                name: params.data.name,
                users: params.data.users.ids,
                app: params.data.app,
                isArchived: !!params.data.isArchived,
              },
            };
            break;
          case 'Genre':
            variables = {
              input: {
                id: params.data.id,
                name: params.data.name,
                value: params.data.value,
                isHidden: !!params.data.isHidden,
              },
            };
            break;
          case 'Office':
            variables = {
              input: {
                id: params.data.id,
                name: params.data.name,
                clientOfficeId: params.data.clientOfficeId,
                clientId: params.data.client.id,
                locationId: params.data.location.id,
                isFcmEligible: params.data.isFcmEligible,
                isAppEligible: params.data.isAppEligible,
              },
            };
            break;
          case 'Feature':
            variables = {
              input: {
                id: params.data.id,
                name: params.data.name,
                description: params.data.description || null,
                defaultState: params.data.defaultState,
                requiresFeatureFlagKey:
                  params.data.requiresFeatureFlagKey || null,
                isArchived: !!params.data.isArchived,
              },
            };
            break;
          case 'FeatureGrant':
            // Identity is immutable; only scopeId (non-client) + state edit.
            variables = {
              input: {
                id: params.data.id,
                scopeId: params.data.scopeId || null,
                state: params.data.state,
              },
            };
            break;
          case 'Studio':
            variables = {
              input: {
                ...params.data,
                __typename: undefined,
                locations: undefined,
                mboContractMappings: undefined,
                client: undefined,
                isVirtual: !!params.data.isVirtual,
                isCentral: !!params.data.isCentral,
                isArchived: !!params.data.isArchived,
                canGrantEligibility: !!params.data.canGrantEligibility,
                clientId: params.data.client.id,
              },
            };
            break;
          case 'CoachLocation':
            variables = {
              input: {
                ...omit(params.data, ['gymImage', 'url']),
                isHeadquarters: params.data.isHeadquarters ?? false,
                __typename: undefined,
                gymImageId: updateOrDeleteImage(params.data.gymImage),
                studio: undefined,
                coaches: undefined,
                defaultPostConnectionInstructions: undefined,
                studioId: params.data.studio.id,
              },
            };
            break;
          case 'Client':
            variables = {
              input: {
                id: params.data.id,
                name: params.data.name,
                displayName: params.data.displayName,
                contactEmail: params.data.contactEmail,
                calendarPlatform: params.data.calendarPlatform,
                collectsMemberData: params.data.collectsMemberData,
                collectsMemberDataForEEP: params.data.collectsMemberDataForEEP,
                videoPlatform: params.data.videoPlatform,
                usesPersonalTraining: params.data.usesPersonalTraining,
                usesHighPerformanceCoaching:
                  params.data.usesHighPerformanceCoaching,
                usesFitnessCenterForEligibility:
                  params.data.usesFitnessCenterForEligibility,
                requiresEligibleEmailForGym:
                  params.data.requiresEligibleEmailForGym,
                usesEligibilityServiceType:
                  params.data.usesEligibilityServiceType,
                usesMboSso: params.data.usesMboSso,
                usesCustomChallenges: params.data.usesCustomChallenges,
                serviceSlotIncrement: params.data.serviceSlotIncrement,
                allowsCoachesToCreateMembers:
                  params.data.allowsCoachesToCreateMembers,
                needsUsToFixTheirUserNames:
                  params.data.needsUsToFixTheirUserNames,
                strictAppCheck: params.data.strictAppCheck,
                isFcmOnly: params.data.isFcmOnly,
                logoId: updateOrDeleteImage(params.data.logo),
                isArchived: params.data.isArchived,
                maxFamilyMembers: params.data.maxFamilyMembers,
                inactivityDuration: params.data.inactivityDuration
                  ? Duration.ofMinutes(params.data.inactivityDuration)
                  : null,
                biometricsEnabled: params.data.biometricsEnabled,
                biometricHardSignOutDuration:
                  params.data.biometricHardSignOutDuration ?? null,
                biometricInactivityTimeout:
                  params.data.biometricInactivityTimeout ?? null,
              },
            };
            break;
          case 'User':
            variables = {
              input: {
                id: params.data.id,
                email: params.data.email,
                firstName: params.data.firstName,
                lastName: params.data.lastName,
                active: params.data.active,
                isAdmin: params.data.isAdmin,
                isCoach: params.data.isCoach,
                isMegaAdmin: params.data.isMegaAdmin,
                isReadOnlyAdmin: params.data.isReadOnlyAdmin,
                isSalesTeam: params.data.isSalesTeam,
                isTestUser: params.data.isTestUser,
                hasHighPerformanceCoaching:
                  params.data.hasHighPerformanceCoaching,
                isExerciseEditor: params.data.isExerciseEditor,
                isTestDataCreator: params.data.isTestDataCreator,
                fitClientId: params.data.fitClient?.id,
              },
              userId: params.data.id,
              clientId: params.data.client?.id ?? '',
              updateCoachData: params.data.isCoach,
              updateAvatar:
                typeof updateOrDeleteImage(params.data.avatar) !== 'undefined',
              avatarInput: {
                avatarId: updateOrDeleteImage(params.data.avatar),
                userId: params.data.id,
              },
              coachDataInput: {
                id: params.data.id,
                bio: params.data.coachData?.bio ?? '',
                titles: params.data.coachData?.titles ?? [],
                passions: params.data.coachData?.passions ?? [],
                type: params.data.coachData?.type ?? CoachType.Coach,
                coachingStyle: params.data.coachData?.coachingStyle,
                degrees: params.data.coachData?.degrees ?? [],
                coachClients: formatCoachClientsUpdates(params.data),
                locationIds: params.data.coachData?.locations?.ids ?? [],
                bodyPhotoId: updateOrDeleteImage(
                  params.data.coachData?.bodyPhoto,
                ),
                headshotPhotoId: updateOrDeleteImage(
                  params.data.coachData?.headshot,
                ),
                supportedCountries:
                  typeof params.data.coachData?.supportedCountriesAlpha2s ===
                  'object'
                    ? params.data.coachData?.supportedCountriesAlpha2s
                    : undefined,
                supportedGoals:
                  typeof params.data.coachData?.supportGoalTypes === 'object'
                    ? params.data.coachData.supportGoalTypes
                    : undefined,
              },
            };
            break;
          case 'Domain':
            variables = {
              input: {
                id: params.data.id,
                domain: params.data.domain,
                clientId: params.data.client?.id ?? undefined,
              },
            };
            break;
          case 'EligibleEmailAddress': {
            const toInput = (data: typeof params.data) => ({
              id: data.id,
              isArchived: !!data.isArchived,
              archivedAt: data.isArchived
                ? (data.archivedAt ?? undefined)
                : undefined,
              isStaff: !!data.isStaff,
              email: data.email,
              ...toHpcEligibilityInput(data),
              officeId: data.office?.id ?? null,
              isFcmEligible: data.manualEligibility
                ? data.isFcmEligible
                : undefined,
              isAppEligible: data.manualEligibility
                ? data.isAppEligible
                : undefined,
            });
            const input = toInput(params.data);
            const previousInput = toInput(params.previousData);

            variables = {
              input: {
                ...pickBy(
                  input,
                  (value, key: keyof typeof input) =>
                    !isEqual(value, previousInput[key]),
                ),
                id: input.id,
              },
            };
            break;
          }
          case 'Cohort': {
            const toInput = (data: typeof params.data) => ({
              id: data.id,
              name: data.name,
              startDate: data.startDate,
              endDate: data.endDate,
              coachIds: data.coaches?.ids ?? [],
            });
            const input = toInput(params.data);
            const previousInput = toInput(params.previousData);

            variables = {
              input: {
                ...pickBy(
                  input,
                  (value, key: keyof typeof input) =>
                    !isEqual(value, previousInput[key]),
                ),
                id: input.id,
              },
            };
            break;
          }
          case 'Challenge':
            variables = {
              input: {
                id: params.data.id,
                title: params.data.title,
                subtitle: params.data.subtitle,
                enabled: params.data.enabled,
                isArchived: params.data.isArchived,
                featured: params.data.featured,
                description: params.data.description,
                clientIds: params.data.clients?.ids ?? [],
                badgeId: updateOrDeleteImage(params.data.badge),
                backgroundId: updateOrDeleteImage(params.data.background),
                teams: (params.data.teams?.nodes ?? []).map((t: any) =>
                  omit(t, ['__typename']),
                ),
                distanceUnit:
                  params.data.challengeType === 'Distance'
                    ? DistanceUnit.Miles
                    : undefined,
                // the API rejects these once a challenge is enabled, so only send edits
                ...pickBy(
                  {
                    startDate: params.data.startDate,
                    endDate: params.data.endDate,
                    goal: params.data.goal,
                  },
                  (value, key) => !isEqual(value, params.previousData[key]),
                ),
              },
            };
            break;
          case 'Exercise': {
            const {
              exerciseBase,
              isArchived: _isArchived,
              ...exerciseUpdateRest
            } = params.data;
            variables = {
              input: {
                ...exerciseUpdateRest,
                apmlId: undefined,
                isDeleted: undefined,
                defaultUnit: params.data.defaultUnit?.value,
                exerciseBaseId:
                  'exerciseBase' in params.data
                    ? exerciseBase === null
                      ? null
                      : exerciseBase?.id
                    : undefined,
                video:
                  'video' in params.data
                    ? updateOrDeleteImage(params.data.video)
                    : undefined,
                thumbnail:
                  'thumbnail' in params.data
                    ? updateOrDeleteImage(params.data.thumbnail)
                    : undefined,
                __typename: undefined,
              },
            };
            break;
          }
          case 'MboContractMapping': {
            variables = {
              input: {
                id: params.data.id,
                mboServiceId: params.data.mboServiceId,
                employeeType: params.data.employeeType,
                studioId: params.data.studio.id,
              },
            };
            break;
          }
          case 'ExerciseTypeCategoryGroup': {
            variables = {
              input: {
                id: params.data.id,
                name: params.data.name,
                archivedAt: params.data.archivedAt,
              },
            };
            break;
          }
          case 'ExerciseTypeCategory': {
            variables = {
              input: {
                id: params.data.id,
                name: params.data.name,
                exerciseTypeCategoryGroupId:
                  params.data.exerciseTypeCategoryGroup?.id ??
                  params.data.exerciseTypeCategoryGroupId,
                archivedAt: params.data.archivedAt,
              },
            };
            break;
          }
          case 'ExerciseType': {
            variables = {
              input: {
                id: params.data.id,
                name: params.data.name,
                exerciseTypeCategoryId:
                  params.data.exerciseTypeCategory?.id ??
                  params.data.exerciseTypeCategoryId,
                archivedAt: params.data.archivedAt,
              },
            };
            break;
          }
          case 'ExerciseBase': {
            variables = {
              input: {
                id: params.data.id,
                name: params.data.name,
                progression: params.data.progression,
                exerciseTypeId:
                  params.data.exerciseType?.id ?? params.data.exerciseTypeId,
                archivedAt: params.data.archivedAt,
              },
            };
            break;
          }
          case 'TrainingPlan': {
            variables = {
              input: {
                id: params.data.id,
                name: params.data.name,
                difficulty: params.data.difficulty,
                frequency: params.data.frequency,
                durationMinutes: params.data.durationMinutes,
                phase: params.data.phase,
                type: params.data.type?.value ?? null,
                equipmentCategory: params.data.equipmentCategory ?? null,
                hidden: params.data.hidden,
                weeks: params.data.weeks,
              },
            };
            break;
          }
          default:
            variables = {
              input: { ...params.data, __typename: undefined },
            };
        }
        return {
          query,
          variables,
          parseResponse: (response: any): CreateResult => {
            switch (resource.type.name) {
              case 'User':
                return {
                  data: userFormatter(response.data.data.user),
                };
              case 'ChatRoom': {
                return {
                  data: {
                    ...response.data.data[camelCase(resource.type.name)],
                    users: {
                      ...response.data.data[camelCase(resource.type.name)]
                        .users,
                      ids: response.data.data[
                        camelCase(resource.type.name)
                      ].users.nodes.map(({ id }: Node) => id),
                    },
                  },
                };
              }
              case 'Client':
                return {
                  data: clientFormatter(response.data.data.client),
                };
              case 'Exercise':
                return {
                  data: formatExerciseRecord(
                    response.data.data[camelCase(resource.type.name)],
                  ),
                };
              case 'Cohort':
                return { data: cohortFormatter(response.data.data.cohort) };
              case 'EligibleEmailAddress':
                return {
                  data: eligibleEmailFormatter(
                    response.data.data.eligibleEmailAddress,
                  ),
                };
              case 'TrainingPlan':
                return { data: response.data.data };
              default:
                return {
                  data: response.data.data[camelCase(resource.type.name)],
                };
            }
          },
        };
      }
      case 'DELETE': {
        let deleteVariables;

        switch (resource.type.name) {
          case 'Client':
            deleteVariables = { clientId: params.id };
            break;
          case 'Domain':
            deleteVariables = { id: params.id };
            break;
          case 'Studio':
            deleteVariables = { id: params.id };
            break;
          case 'User':
            deleteVariables = {
              userId: params.id,
              forgetIterable: params?.meta?.forgetIterable,
            };
            break;
          case 'Challenge':
            deleteVariables = { id: params.id };
            break;
          case 'EligibleEmailAddress':
            deleteVariables = { id: params.id };
            break;
          case 'Exercise':
            deleteVariables = { id: params.id };
            break;
          case 'MboContractMapping':
            deleteVariables = { id: params.id };
            break;
          case 'FeatureGrant':
            deleteVariables = { id: params.id };
            break;
          case 'Cohort':
            deleteVariables = { id: params.id };
            break;
          default:
            deleteVariables = {};
        }
        return {
          query,
          variables: deleteVariables,
          parseResponse: (response: any): DeleteResult => ({
            // Grant delete nests the row under `featureGrant`; other resources
            // return it directly.
            data: response.data.data?.featureGrant ?? response.data.data,
          }),
        };
      }
      default:
        throw new Error(`No type found for ${raFetchType} of ${resourceName}`);
    }
  };

export default (() => {
  const listPages: { [key: string]: (PageInfo | undefined)[] | undefined } = {};

  const buildListVariables = (resource: any, params: any) => {
    const { name }: { name: string } = resource.type;
    let filters;

    // Filters
    switch (resource.type.name) {
      case 'Content':
        filters = {
          ...params.filter,
          __typename: undefined,
          ...(params.filter.__typename
            ? { component: params.filter.__typename?.join(',') }
            : { component: contentComponents.join(',') }),
        };
        break;
      case 'Client':
        filters = params.filter.q ? { name: params.filter.q } : params.filter;
        break;
      case 'User':
        filters = params.filter.q ? { email: params.filter.q } : params.filter;
        break;
      case 'Feature':
        // Hide archived features unless the Archived toggle is on.
        filters = {
          isArchived: params.filter?.isArchived ?? false,
        };
        break;
      case 'FeatureGrant':
        filters = {
          ...(params.filter?.featureId
            ? { featureId: params.filter.featureId }
            : {}),
          ...(params.filter?.clientId
            ? { clientId: params.filter.clientId }
            : {}),
          ...(params.filter?.scopeType
            ? { scopeType: params.filter.scopeType }
            : {}),
          ...(params.filter?.scopeId ? { scopeId: params.filter.scopeId } : {}),
        };
        break;
      case 'Challenge':
        filters = {
          // by default, exclude custom challenges and archived challenges
          // this can be overridden by setting the filter
          excludeCustomChallenges: true,
          isArchived: false,
          ...params.filter,
        };
        break;
      case 'Exercise':
        filters = {
          name: params.filter?.name,
          exerciseBaseId: params.filter?.exerciseBaseId,
          isArchived: params.filter?.isArchived,
          exerciseTypeId: params.filter?.exerciseTypeId,
          exerciseTypeCategoryId: params.filter?.exerciseTypeCategoryId,
          exerciseTypeCategoryGroupId:
            params.filter?.exerciseTypeCategoryGroupId,
        };
        break;
      case 'ExerciseTypeCategoryGroup':
        filters = {
          isArchived: params.filter?.isArchived ?? false,
          name: params.filter?.name,
        };
        break;
      case 'ExerciseTypeCategory':
        filters = {
          isArchived: params.filter?.isArchived ?? false,
          name: params.filter?.name,
          exerciseTypeCategoryGroupId:
            params.filter?.exerciseTypeCategoryGroupId,
        };
        break;
      case 'ExerciseType':
        filters = {
          isArchived: params.filter?.isArchived ?? false,
          name: params.filter?.name,
          exerciseTypeCategoryId: params.filter?.exerciseTypeCategoryId,
          exerciseTypeCategoryGroupId:
            params.filter?.exerciseTypeCategoryGroupId,
        };
        break;
      case 'ExerciseBase':
        filters = {
          isArchived: params.filter?.isArchived ?? false,
          name: params.filter?.name,
          exerciseTypeId: params.filter?.exerciseTypeId,
          exerciseTypeCategoryId: params.filter?.exerciseTypeCategoryId,
          exerciseTypeCategoryGroupId:
            params.filter?.exerciseTypeCategoryGroupId,
          progression: params.filter?.progression,
        };
        break;
      case 'AuditLog':
        filters = {
          action: params.filter?.action,
          category: params.filter?.category,
          dataId: params.filter?.dataId,
        };
        break;
      case 'TrainingPlan': {
        filters = {
          ...params.filter,
          id: undefined,
          isArchived: params.filter?.isArchived ?? false,
          isDeleted: params.filter?.isDeleted ?? false,
        };
        break;
      }
      default:
        filters = { ...params.filter };
    }

    const ordering: {
      orderBy?:
        | ContentOrderBy
        | ExerciseBaseOrderBy[]
        | ExerciseOrderBy[]
        | ExerciseTypeCategoryGroupOrderBy[]
        | ExerciseTypeCategoryOrderBy[]
        | ExerciseTypeOrderBy[];
      sort?: {
        field: string;
        order: OrderDirection;
      };
    } = {
      orderBy: undefined,
      sort: undefined,
    };

    // Ordering
    switch (resource.type.name) {
      case 'Content': {
        const order: Record<string, ContentOrder> = {
          firstPublishedAt: ContentOrder.FirstPublishedAt,
          lastPublishedAt: ContentOrder.LastPublishedAt,
          title: ContentOrder.Title,
        };

        if (order[params.sort.field]) {
          ordering.orderBy = {
            order: order[params.sort.field],
            direction: params.sort.order.toLowerCase(),
          };
        }
        break;
      }
      case 'ExerciseTypeCategoryGroup': {
        const order: Record<string, ExerciseTypeCategoryGroupOrder> = {
          name: ExerciseTypeCategoryGroupOrder.Name,
        };

        if (order[params.sort.field]) {
          ordering.orderBy = [
            {
              order: order[params.sort.field],
              direction: params.sort.order.toLowerCase(),
            },
          ];
        }
        break;
      }
      case 'ExerciseTypeCategory': {
        const order: Record<string, ExerciseTypeCategoryOrder> = {
          name: ExerciseTypeCategoryOrder.Name,
        };

        if (order[params.sort.field]) {
          ordering.orderBy = [
            {
              order: order[params.sort.field],
              direction: params.sort.order.toLowerCase(),
            },
          ];
        }
        break;
      }
      case 'ExerciseType': {
        const order: Record<string, ExerciseTypeOrder> = {
          name: ExerciseTypeOrder.Name,
        };

        if (order[params.sort.field]) {
          ordering.orderBy = [
            {
              order: order[params.sort.field],
              direction: params.sort.order.toLowerCase(),
            },
          ];
        }
        break;
      }
      case 'ExerciseBase': {
        const order: Record<string, ExerciseBaseOrder> = {
          name: ExerciseBaseOrder.Name,
          progression: ExerciseBaseOrder.Progression,
        };

        if (order[params.sort.field]) {
          ordering.orderBy = [
            {
              order: order[params.sort.field],
              direction: params.sort.order.toLowerCase(),
            },
          ];
        }
        break;
      }
      case 'Exercise': {
        const order: Record<string, ExerciseOrder> = {
          createdAt: ExerciseOrder.CreatedAt,
          name: ExerciseOrder.Name,
          progression: ExerciseOrder.Progression,
        };

        if (
          params.sort?.field &&
          order[params.sort.field] &&
          params.sort?.order
        ) {
          ordering.orderBy = [
            {
              order: order[params.sort.field],
              direction: params.sort.order.toLowerCase(),
            },
          ];
        } else {
          ordering.orderBy = [
            {
              order: ExerciseOrder.CreatedAt,
              direction: OrderDirection.Desc,
            },
          ];
        }
        break;
      }
      case 'Challenge': {
        ordering.sort = {
          ...params.sort,
          order: params.sort.order.toLowerCase(),
        };
        break;
      }
      default:
    }

    // Pagination
    const { pagination, ...config } = params;
    const { page = 1, perPage } = pagination;
    const { endCursor = '' } = listPages[name]?.[page - 1] ?? {};

    return {
      ...config,
      ...ordering,
      filter: filters,
      input: {
        ...params.input,
        first: perPage,
        after: endCursor,
      },
    };
  };

  const parseListPageInfo = (resource: any, request: any, response: any) => {
    const { name }: { name: string } = resource.type;
    const { page }: { page: number } = request.pagination ?? {};
    const { pageInfo }: { pageInfo?: PageInfo } = response.data?.data ?? {};

    // Update cursor
    const pages = listPages[name] ?? [];
    pages[page] = pageInfo;
    listPages[name] = pages;

    return pageInfo;
  };

  return buildQuery({ buildListVariables, parseListPageInfo });
})();

const resourceNameMapper = (resource: any) => {
  switch (resource.name) {
    case 'Genre':
      // We need this mapping because the type/resource called "Genre" is using CRUD operations with the name "ContentGenre"
      // i.e. the `contentGenres` query doesn't return type ContentGenre, it returns type Genre
      // This all uses introspection which is acceptable magic when the names are consistent but annoying when they're not
      // See also `deleteUser` versus `removeUser` below
      return 'ContentGenre';
    default:
      return resource.name;
  }
};

export const introspection = {
  operationNames: {
    [GET_LIST]: (resource: any) => {
      if (resource.name === 'TrainingPlan') {
        return 'adminTrainingPlanTemplates';
      }
      return pluralize(camelCase(resourceNameMapper(resource)));
    },
    [GET_ONE]: (resource: any) => camelCase(resourceNameMapper(resource)),
    [GET_MANY]: (resource: any) =>
      `many${pluralize(resourceNameMapper(resource))}`,
    [GET_MANY_REFERENCE]: (resource: any) =>
      `all${pluralize(resourceNameMapper(resource))}`,
    [CREATE]: (resource: any) => {
      if (resource.name === 'TrainingPlan') {
        return 'createTrainingPlanTemplate';
      }
      return `create${resourceNameMapper(resource)}`;
    },
    [UPDATE]: (resource: any) => {
      if (resource.name === 'TrainingPlan') {
        return 'updateTrainingPlanTemplate';
      }
      return `update${resourceNameMapper(resource)}`;
    },
    [DELETE]: (resource: any) =>
      resource.name === 'User'
        ? 'deleteUser'
        : `remove${resourceNameMapper(resource)}`,
  },
};
